// Money → Transactions (spec 044 D50–D59, plan slice 7 Task 4): every read the page makes.
// Every table read and every number carries the page's orderBy("createdAt", "desc"), so
// each rides the index its page uses (without it, a count or a sum asks Firestore for
// another index).
import {
  collection,
  doc,
  getAggregateFromServer,
  getCountFromServer,
  getDoc,
  getDocs,
  limit as qlimit,
  orderBy,
  query,
  startAfter,
  sum,
  Timestamp,
  where,
} from "firebase/firestore";
import { db } from "./firebase";
import { adjustmentTotals, collectPage, kindOf, visibleIn } from "../utils/transactionLedger";
import { toDate } from "../utils/ticketPresentation";

const ledger = collection(db, "walletTransactions");
const donationsCol = collection(db, "donations");

const period = ({ from, to }) => [
  where("createdAt", ">=", Timestamp.fromDate(from)),
  where("createdAt", "<", Timestamp.fromDate(to)),
  orderBy("createdAt", "desc"),
];

// D58: each tab's server filter; the browser drops what the kinds table sends elsewhere.
const TAB_FILTER = {
  all: () => [],
  topups: ({ showCheckout }) => [where("status", "in", showCheckout ? ["succeeded", "created"] : ["succeeded"])],
  lessons: () => [where("type", "in", ["debit", "refund"])],
  adjustments: () => [where("transactionType", "==", "adminAdjustment")],
  referrals: () => [where("transactionType", "==", "referralReward")],
};

const batchReader = (col, base, size) => async (after) => {
  const snap = await getDocs(query(col, ...base, ...(after ? [startAfter(after)] : []), qlimit(size)));
  return { docs: snap.docs.map((d) => ({ row: { id: d.id, ...d.data() }, snap: d })), full: snap.docs.length === size };
};

// R5: a page reads batches until pageSize + 1 rows survive the tab's filter. `skipUsers` (a Set
// of uids) also drops those people's rows: Money → Overview leaves the test accounts out (D103).
export const getLedgerPage = ({ tab, range, showCheckout = false, start = null, pageSize = 25, batch = 50, skipUsers = null }) =>
  collectPage({
    fetchBatch: batchReader(ledger, [...TAB_FILTER[tab]({ showCheckout }), ...period(range)], batch),
    isVisible: (r) => visibleIn(tab, kindOf(r), { showCheckout }) && !(skipUsers && skipUsers.has(r.userId)),
    start,
    pageSize,
  });

export const getDonationsPage = ({ range, start = null, pageSize = 25, batch = 50 }) =>
  collectPage({ fetchBatch: batchReader(donationsCol, period(range), batch), isVisible: () => true, start, pageSize });

// D36: a number that fails is null (and logged); the page shows "—" for it and for anything
// built on it, and every other number still lands.
const settle = (what, promise) =>
  promise.catch((e) => {
    console.error(`Transactions: ${what} failed:`, e);
    return null;
  });

// R17 (staging shape check, 2026-09-28): Firestore serves sum() only from an index holding
// the summed field after createdAt (app PR #110), and such an index holds only the rows that
// have that field. So every sum is its own query, apart from its count: a top-up with no
// `hours` still counts, and still counts in Collected (£).
export const getLedgerAggregates = async ({ range, withDonations = false }) => {
  const count = (what, col, ...filters) =>
    settle(what, getCountFromServer(query(col, ...filters, ...period(range))).then((s) => s.data().count));
  const total = (what, field, ...filters) =>
    settle(
      what,
      getAggregateFromServer(query(ledger, ...filters, ...period(range)), { total: sum(field) }).then((s) => s.data().total)
    );
  const paid = where("status", "==", "succeeded");
  const debit = where("type", "==", "debit");
  const refund = where("type", "==", "refund");
  const referral = where("transactionType", "==", "referralReward");
  const [all, paidN, gbp, hours, checkout, tutorPay, debitN, debitS, refundN, refundS, referralN, referralS, adjustments, donations] =
    await Promise.all([
      count("count all", ledger),
      count("count paid", ledger, paid),
      total("sum collected", "amount", paid),
      total("sum sessions sold", "hours", paid),
      count("count checkout", ledger, where("status", "==", "created")),
      count("count tutor pay", ledger, where("type", "==", "payout")),
      count("count debit", ledger, debit),
      total("sum debit", "amount", debit),
      count("count refund", ledger, refund),
      total("sum refund", "amount", refund),
      count("count referral", ledger, referral),
      total("sum referral", "amount", referral),
      // R6: adjustments are few, so they are read as rows and split by type here.
      // ponytail: a (transactionType, type, createdAt) index past 1,000 adjustments a period.
      settle(
        "adjustments",
        getDocs(query(ledger, where("transactionType", "==", "adminAdjustment"), ...period(range), qlimit(1000))).then((s) =>
          adjustmentTotals(s.docs.map((d) => d.data()))
        )
      ),
      withDonations ? count("count donations", donationsCol) : Promise.resolve(null),
    ]);
  return {
    all,
    paid: { n: paidN, gbp, sessions: hours },
    checkout,
    debit: { n: debitN, sessions: debitS },
    refund: { n: refundN, sessions: refundS },
    tutorPay,
    referral: { n: referralN, sessions: referralS },
    adjustments,
    donations,
  };
};

// R7: one person's rows for the period (the existing (userId, createdAt desc) index); the
// tabs, counts and cards come from these rows in the browser.
// ponytail: cursor pages per tab past 1,000 rows a person.
export const getPersonLedger = async ({ uid, range, cap = 1000 }) => {
  const snap = await getDocs(query(ledger, where("userId", "==", uid), ...period(range), qlimit(cap)));
  return { rows: snap.docs.map((d) => ({ id: d.id, ...d.data() })), capped: snap.docs.length === cap };
};

// A student's donations (admins only), by the single-field studentId index; the period and
// the order are applied here, so no composite index is needed.
export const getPersonDonations = async ({ uid, range }) => {
  const snap = await getDocs(query(donationsCol, where("studentId", "==", uid), qlimit(500)));
  const at = (d) => toDate(d.createdAt);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((d) => at(d) && at(d) >= range.from && at(d) < range.to)
    .sort((a, b) => at(b) - at(a));
};

// R8: "where is this payment" — the row stored under the Stripe id and every row naming it,
// checkout rows included. The period and the tab do not apply. Call only after isStripeId().
export const findStripePayment = async (pi) => {
  const [one, many] = await Promise.all([
    getDoc(doc(ledger, pi)),
    getDocs(query(ledger, where("paymentIntentId", "==", pi), qlimit(10))),
  ]);
  const byId = new Map(many.docs.map((d) => [d.id, { id: d.id, ...d.data() }]));
  if (one.exists()) byId.set(one.id, { id: one.id, ...one.data() });
  const at = (r) => toDate(r.createdAt)?.getTime() || 0;
  return [...byId.values()].sort((a, b) => at(b) - at(a));
};

// R9: the tab's own rows to the end of the period, the same rows hidden, up to `cap`.
export const readForExport = async ({ tab, range, showCheckout = false, cap = 10000 }) => {
  const rows = [];
  let start = null;
  for (;;) {
    const page =
      tab === "donations"
        ? await getDonationsPage({ range, start, pageSize: 500, batch: 500 })
        : await getLedgerPage({ tab, range, showCheckout, start, pageSize: 500, batch: 500 });
    rows.push(...page.rows);
    if (!page.hasMore) return { rows: rows.slice(0, cap), capped: rows.length > cap };
    if (rows.length >= cap) return { rows: rows.slice(0, cap), capped: true };
    start = page.next;
  }
};
